import { useState } from 'react';
import { Blocks, FileText, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Modal,
  SettingsPageHeader,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { BUTTON_GHOST, BUTTON_PRIMARY } from '@/shared/ui-class';
import { UPLOAD_FOLDER_ICON } from '@/shared/upload-icons';
import {
  SEED_PERSONAL_SKILLS,
  SEED_PLATFORM_SKILLS,
  SEED_PLUGIN_SKILLS,
  type PluginSkillSeed,
  type SettingsSkillSeed,
} from '@/versions/v1/shared/settings-seed';
import { formatRelativeTime } from './format-utils';
import { PlatformSkillInfo } from './PlatformSkillInfo';

const SKILL_LIST_CARD_CLASS =
  'flex flex-col overflow-hidden rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface)] shadow-sm backdrop-blur-lg';

export function SkillsScene() {
  const [personalSkills, setPersonalSkills] = useState<SettingsSkillSeed[]>(
    () => [...SEED_PERSONAL_SKILLS],
  );
  const [platformSkills] = useState<SettingsSkillSeed[]>(
    () => [...SEED_PLATFORM_SKILLS],
  );
  const [pluginSkills] = useState(() => [...SEED_PLUGIN_SKILLS]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const isEmpty =
    personalSkills.length === 0 &&
    platformSkills.length === 0 &&
    pluginSkills.length === 0;

  const handleCreate = () => {
    const slug = newName.trim().toLowerCase().replace(/\s+/g, '-');
    if (!slug) return;
    setPersonalSkills((prev) => [
      {
        slug,
        description: newDescription.trim() || 'No description',
        source: 'user',
        updatedAt: Date.now(),
      },
      ...prev,
    ]);
    setNewName('');
    setNewDescription('');
    setCreateOpen(false);
  };

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="SkillsScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
            <div className="space-y-8">
              <SettingsPageHeader
                title="Skills"
                description="Small markdown files that teach the agent how to handle specific tasks."
                actions={
                  <>
                    <SkillUploadFolderTrigger />
                    <SkillCreateButton onClick={() => setCreateOpen(true)} />
                  </>
                }
              />
              <SkillUploadFolderFollowup />
            </div>

            {isEmpty ? (
              <SkillsEmptyState onCreate={() => setCreateOpen(true)} />
            ) : (
              <>
                {personalSkills.length > 0 ? (
                  <Commentable name="PersonalSkills">
                    <SkillListCard skills={personalSkills} />
                  </Commentable>
                ) : null}

                {platformSkills.length > 0 ? (
                  <Commentable name="PlatformSkills">
                    <section className="flex flex-col gap-3">
                      <div className="flex flex-col gap-0.5">
                        <h2 className="text-sm font-medium uppercase tracking-wide text-muted">
                          Platform skills
                        </h2>
                        <p className="text-sm text-muted">
                          Managed by your workspace — always available.
                        </p>
                      </div>
                      <SkillListCard skills={platformSkills} />
                    </section>
                  </Commentable>
                ) : null}

                {pluginSkills.length > 0 ? (
                  <Commentable name="PluginSkills">
                    <section className="flex flex-col gap-3">
                      <div className="flex flex-col gap-0.5">
                        <h2 className="text-sm font-medium uppercase tracking-wide text-muted">
                          Plugin skills
                        </h2>
                        <p className="text-sm text-muted">
                          Bundled by installed plugins — read-only.
                        </p>
                      </div>
                      <ul className={SKILL_LIST_CARD_CLASS}>
                        {pluginSkills.map((pluginSkill, idx) => (
                          <PluginSkillRow
                            key={`${pluginSkill.pluginId}:${pluginSkill.name}`}
                            skill={pluginSkill}
                            isFirst={idx === 0}
                          />
                        ))}
                      </ul>
                    </section>
                  </Commentable>
                ) : null}
              </>
            )}

            <Modal open={createOpen} onOpenChange={setCreateOpen} title="New skill">
              <div className="space-y-4 px-6 pb-6">
                <div>
                  <label
                    htmlFor="skill-name"
                    className="text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Name
                  </label>
                  <input
                    id="skill-name"
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="my-skill"
                    className="mt-1 w-full rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus"
                  />
                </div>
                <div>
                  <label
                    htmlFor="skill-description"
                    className="text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Description
                  </label>
                  <textarea
                    id="skill-description"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateOpen(false)}
                    className="rounded-lg border border-divider px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreate}
                    className={BUTTON_PRIMARY}
                  >
                    Create
                  </button>
                </div>
              </div>
            </Modal>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

function SkillUploadFolderTrigger() {
  return (
    <button
      type="button"
      disabled
      title="Upload skill is not available in this mock"
      className={BUTTON_GHOST}
    >
      <UPLOAD_FOLDER_ICON className="h-4 w-4" />
      <span>Upload skill</span>
    </button>
  );
}

function SkillCreateButton({
  onClick,
  variant = 'primary',
}: {
  onClick: () => void;
  variant?: 'primary' | 'ghost';
}) {
  const className = variant === 'primary' ? BUTTON_PRIMARY : BUTTON_GHOST;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="New skill"
      className={className}
    >
      <Plus className="h-4 w-4" aria-hidden />
      New skill
    </button>
  );
}

function SkillUploadFolderFollowup() {
  return (
    <div className="flex flex-col gap-2">
      <details className="text-xs text-muted">
        <summary className="cursor-pointer select-none">Folder requirements</summary>
        <ul className="mt-2 list-disc space-y-1 pl-4">
          <li>
            Exactly one <code>SKILL.md</code> per folder; its frontmatter{' '}
            <code>name</code> becomes the skill&apos;s slug.
          </li>
          <li>
            Assets install only from <code>references/</code> and{' '}
            <code>scripts/</code> inside the SKILL.md directory; anything else
            (README, source files) is skipped and reported — not an error.
          </li>
          <li>
            Per asset: up to 512 KB; at most 20 assets. Text, images, and
            scripts are accepted; SVG is not.
          </li>
          <li>SKILL.md body up to 64 KB.</li>
          <li>
            Whole upload: up to 200 files / 10 MB total. Hidden files and{' '}
            <code>node_modules</code> are excluded automatically before these
            limits are counted.
          </li>
        </ul>
      </details>
      <div
        role="region"
        aria-label="Skill folder drop zone"
        className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-300 px-6 py-10 text-center transition-colors dark:border-zinc-700"
      >
        <UPLOAD_FOLDER_ICON className="h-7 w-7 text-zinc-400 dark:text-zinc-500" />
        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
          Drag a skill folder here
        </p>
        <p className="text-xs text-muted">
          or use <span className="font-medium">Upload skill</span> above — the
          folder must contain a <code className="text-xs">SKILL.md</code>.
        </p>
      </div>
    </div>
  );
}

function SkillListCard({ skills }: { skills: SettingsSkillSeed[] }) {
  return (
    <ul className={SKILL_LIST_CARD_CLASS}>
      {skills.map((skill, idx) => (
        <SkillRow key={skill.slug} skill={skill} isFirst={idx === 0} />
      ))}
    </ul>
  );
}

function SkillRow({
  skill,
  isFirst,
}: {
  skill: SettingsSkillSeed;
  isFirst: boolean;
}) {
  const isPlatform = skill.source === 'platform';

  return (
    <li
      className={
        isFirst
          ? 'group relative transition-colors hover:bg-[var(--glass-surface-strong)]'
          : 'group relative border-t border-[var(--glass-border)] transition-colors hover:bg-[var(--glass-surface-strong)]'
      }
    >
      <Link
        to={`/settings/skills/${skill.slug}`}
        className="absolute inset-0 z-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-600)_60%,transparent)]"
        aria-label={`Open skill ${skill.slug}`}
      />
      <div className="relative z-10 flex items-start gap-3 px-4 py-3.5 pointer-events-none">
        <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-zinc-400 dark:text-zinc-500" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {skill.slug}
              </span>
              {isPlatform ? (
                <span className="pointer-events-auto inline-flex items-center gap-1">
                  <Pill>Platform</Pill>
                  <PlatformSkillInfo skillSlug={skill.slug} />
                </span>
              ) : null}
            </div>
            <span className="flex-shrink-0 text-xs text-zinc-400 dark:text-zinc-500">
              {formatRelativeTime(skill.updatedAt)}
            </span>
          </div>
          <p className="mt-0.5 truncate text-sm text-muted">
            {skill.description || (
              <span className="italic text-zinc-400 dark:text-zinc-600">
                No description
              </span>
            )}
          </p>
        </div>
      </div>
    </li>
  );
}

function PluginSkillRow({
  skill,
  isFirst,
}: {
  skill: PluginSkillSeed;
  isFirst: boolean;
}) {
  return (
    <li
      className={
        isFirst
          ? 'transition-colors hover:bg-[var(--glass-surface-strong)]'
          : 'border-t border-[var(--glass-border)] transition-colors hover:bg-[var(--glass-surface-strong)]'
      }
    >
      <Link
        to={`/settings/skills/plugin/${skill.pluginId}/${skill.name}`}
        className="flex items-start gap-3 px-4 py-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-600)_60%,transparent)]"
      >
        <Blocks className="mt-0.5 h-4 w-4 flex-shrink-0 text-zinc-400 dark:text-zinc-500" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {skill.name}
            </span>
            <Pill>{`plugin:${skill.pluginName}`}</Pill>
          </div>
          <p className="mt-0.5 truncate text-sm text-muted">
            {skill.description || (
              <span className="italic text-zinc-400 dark:text-zinc-600">
                No description
              </span>
            )}
          </p>
        </div>
      </Link>
    </li>
  );
}

function SkillsEmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-[var(--glass-border-strong)] bg-[var(--glass-surface)] px-6 py-12 text-center shadow-sm backdrop-blur-lg">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
        <FileText className="h-5 w-5" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          No skills yet
        </p>
        <p className="max-w-sm text-sm text-muted">
          Skills teach the agent how to handle specific tasks. Create one to
          get started.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled
          title="Upload skill is not available in this mock"
          className={BUTTON_GHOST}
        >
          <UPLOAD_FOLDER_ICON className="h-4 w-4" />
          <span>Upload skill</span>
        </button>
        <SkillCreateButton onClick={onCreate} variant="ghost" />
      </div>
    </div>
  );
}
